import { useState, useEffect } from "react";
import { Store, Phone, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useConfig } from "./ConfiguracoesLayout";
import ConfigSection, { inputClass } from "./ConfigSection";
import SlugEditor from "@/components/SlugEditor";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";

const InformacoesGerais = () => {
  const { form, update, restaurant } = useConfig();
  const { toast } = useToast();
  const [slug, setSlug] = useState(restaurant?.slug || "");
  const [slugValid, setSlugValid] = useState(true);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [checkingPhone, setCheckingPhone] = useState(false);

  const handleSlugChange = async (newSlug: string) => {
    setSlug(newSlug);
  };

  // Slug is saved via the main save button in ConfiguracoesLayout
  // We need to expose slug to the parent - we update the form
  const handleSlugBlur = async () => {
    if (slugValid && slug && slug !== restaurant?.slug && restaurant) {
      const { error } = await supabase.from("restaurants")
        .update({ slug })
        .eq("id", restaurant.id);
      if (error) {
        toast({ title: "Erro ao atualizar URL", description: error.message, variant: "destructive" });
        setSlug(restaurant.slug);
      } else {
        toast({ title: "URL do cardápio atualizada!" });
      }
    }
  };

  const handlePhoneChange = async (newPhone: string) => {
    update("phone", newPhone);
    setPhoneError(null);

    // Only check if phone is not empty and has changed
    if (!newPhone || !restaurant) return;
    
    setCheckingPhone(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: isAvailable } = await supabase.rpc("is_phone_available", { 
        check_phone: newPhone,
        exclude_user_id: user.id
      });
      
      if (!isAvailable) {
        setPhoneError("Este número de telefone já está sendo usado por outro usuário.");
      }
    } catch (error) {
      console.error("Error checking phone:", error);
    } finally {
      setCheckingPhone(false);
    }
  };

  return (
    <ConfigSection title="Informações Gerais" icon={Store}>
      <div className="space-y-2">
        <Label>Nome do restaurante *</Label>
        <Input value={form.name} onChange={(e) => update("name", e.target.value)} className={inputClass} />
      </div>
      <div className="space-y-2">
        <Label>Descrição</Label>
        <textarea
          value={form.description}
          onChange={(e) => update("description", e.target.value)}
          rows={3}
          className="w-full rounded-md bg-card border border-border px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          placeholder="Descreva seu restaurante..."
        />
      </div>

      <div onBlur={handleSlugBlur}>
        <SlugEditor
          value={slug}
          onChange={handleSlugChange}
          restaurantId={restaurant?.id}
          restaurantName={form.name}
          onValidityChange={setSlugValid}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>CNPJ</Label>
          <Input value={form.cnpj} onChange={(e) => update("cnpj", e.target.value)} className={inputClass} placeholder="00.000.000/0000-00" />
        </div>
        <div className="space-y-2">
          <Label className="flex items-center gap-1">
            <Phone className="h-3.5 w-3.5" />
            WhatsApp do Restaurante *
          </Label>
          <Input 
            value={form.phone} 
            onChange={(e) => handlePhoneChange(e.target.value)} 
            className={`${inputClass} ${phoneError ? "border-destructive" : ""}`}
            placeholder="(00) 00000-0000" 
            disabled={checkingPhone}
          />
          {phoneError && (
            <Alert variant="destructive" className="mt-2 py-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">{phoneError}</AlertDescription>
            </Alert>
          )}
          {!phoneError && (
            <p className="text-[11px] text-muted-foreground">
              Usado para receber confirmações de pedidos via WhatsApp. Apenas um número por conta.
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => update("is_active", e.target.checked)}
            className="w-4 h-4 rounded border-border accent-primary"
          />
          <span className="text-sm font-medium">Restaurante ativo (visível no cardápio digital)</span>
        </label>
      </div>
      <div className="space-y-2">
        <Label>Mensagem de agradecimento no cupom de entrega</Label>
        <textarea
          value={form.receipt_thank_you_message || ""}
          onChange={(e) => update("receipt_thank_you_message", e.target.value)}
          rows={2}
          className="w-full rounded-md bg-card border border-border px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          placeholder="Ex: Obrigado pela preferência! Volte sempre 😊"
        />
        <p className="text-[11px] text-muted-foreground">
          Aparece no cupom do entregador. Deixe vazio para não exibir.
        </p>
      </div>
    </ConfigSection>
  );
};

export default InformacoesGerais;
